import { AuthApiSchema } from './AuthApiSchema';

/**
 * A single error detail from the Auth API.
 */
export interface AuthApiErrorResponseDetail {
    /** A stable, machine-readable code identifying the kind of error, so a UI can match on it. */
    code: string;

    /** Path of the request body that produced the error. */
    path: string | null;
}

/**
 * An error response from the Auth API.
 */
export class AuthApiErrorResponse extends Error {
    /** The response status. */
    readonly statusCode: number;

    /** Identifier for tracing the request in log messages. */
    readonly correlationId: string | null;

    /** All errors that occurred during the request. */
    readonly errors: AuthApiErrorResponseDetail[];

    constructor(statusCode: number, correlationId: string | null, errors: AuthApiErrorResponseDetail[] = []) {
        super('Auth API responded with an error.');

        this.errors = errors;
        this.correlationId = correlationId;
        this.statusCode = statusCode;

        this.name = 'AuthApiErrorResponse';
    }

    /**
     * Create a new AuthApiErrorResponse from an HTTP Response.
     * @param response The failed response to build the error from.
     * @returns The created error.
     */
    static async fromResponse(response: Response): Promise<AuthApiErrorResponse> {
        // Try and decode the body to check if this is an actual error response
        let body: unknown;

        try {
            body = await response.json();
        } catch {
            body = null;
        }

        const errors: AuthApiErrorResponseDetail[] = [];

        if (body !== null && typeof body === 'object' && 'errors' in body && Array.isArray((body as { errors: unknown }).errors)) {
            for (const error of (body as { errors: unknown[] }).errors) {
                if (AuthApiErrorResponse.isErrorResponseDetailDto(error)) {
                    errors.push({
                        code: error.code,
                        path: error.path,
                    });
                }
            }
        }

        return new AuthApiErrorResponse(response.status, response.headers.get('X-Correlation-Id'), errors);
    }

    /**
     * Checks if the specified error is an AuthApiErrorResponse.
     * @param error The error to check.
     * @returns True if the error is an AuthApiErrorResponse.
     */
    static is(error: unknown): error is AuthApiErrorResponse {
        return error instanceof AuthApiErrorResponse;
    }

    /**
     * Checks if the specified error is an ErrorResponseDetailDto.
     * @param error The error to check.
     * @returns True if the error matches the DTO shape.
     */
    private static isErrorResponseDetailDto(error: unknown): error is AuthApiSchema['ErrorResponseDetailDto'] {
        return error !== null && typeof error === 'object' && 'code' in error && 'path' in error;
    }
}
